
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Prompt } from "@/services/promptService";

interface FetchPromptsOptions {
  sortBy?: string;
  timeFilter?: string;
}

/**
 * Counts the total number of approved prompts
 */
export const countTotalPrompts = async (): Promise<number> => {
  try {
    console.log("Counting total prompts");
    const { count, error } = await supabase
      .from('prompts')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'approved');
    
    if (error) {
      console.error('Error counting prompts:', error);
      throw error;
    }
    
    console.log(`Total prompt count: ${count || 0}`);
    return count || 0;
  } catch (error) {
    console.error('Error in countTotalPrompts:', error);
    return 0;
  }
};

/**
 * Counts the filtered prompts for a specific category
 */
export const countFilteredPrompts = async (categoryId: string): Promise<number> => {
  try {
    console.log(`Counting prompts for category: ${categoryId}`);
    const { count, error } = await supabase
      .from('prompts')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'approved')
      .eq('category_id', categoryId);
    
    if (error) {
      console.error('Error getting filtered count:', error);
      throw error;
    }
    
    console.log(`Filtered prompt count for category ${categoryId}: ${count || 0}`);
    return count || 0;
  } catch (error) {
    console.error('Error in countFilteredPrompts:', error);
    return 0;
  }
};

/**
 * Fetches prompts with pagination and optional category filter
 */
export const fetchPrompts = async (
  currentPage: number,
  itemsPerPage: number,
  categoryId?: string | null,
  options: FetchPromptsOptions = {}
): Promise<Prompt[]> => {
  try {
    const { sortBy = 'recent', timeFilter = '' } = options; // Changed default from 'relevance' to 'recent'
    
    console.log('Loading prompts with filters:', { 
      category: categoryId, 
      page: currentPage,
      itemsPerPage: itemsPerPage,
      sortBy,
      timeFilter
    });
    
    // Calculate pagination range
    const from = (currentPage - 1) * itemsPerPage;
    console.log(`Pagination: from=${from}, to=${from + itemsPerPage - 1}`);
    
    // Build the query
    let query = supabase
      .from('prompts')
      .select(`
        id, 
        title, 
        description, 
        content,
        likes,
        reviews,
        copies,
        created_at,
        user_id,
        category_id,
        categories(id, name, slug)
      `, { count: 'exact' })
      .eq('status', 'approved');
    
    // Add category filter
    if (categoryId) {
      console.log(`Filtering by category: ${categoryId}`);
      query = query.eq('category_id', categoryId);
    }
    
    // Add time filter
    if (timeFilter) {
      const now = new Date();
      let timeThreshold = new Date();
      
      switch (timeFilter) {
        case 'week':
          timeThreshold.setDate(now.getDate() - 7);
          break;
        case 'month':
          timeThreshold.setMonth(now.getMonth() - 1);
          break;
        default:
          // No time filter
          break;
      }
      
      if (timeFilter === 'week' || timeFilter === 'month') {
        query = query.gte('created_at', timeThreshold.toISOString());
      }
    }
    
    // Add sorting based on sortBy parameter
    switch (sortBy) {
      case 'popular':
        query = query
          .order('copies', { ascending: false })
          .order('likes', { ascending: false })
          .order('reviews', { ascending: false });
        break;
      case 'recent':
        query = query.order('created_at', { ascending: false });
        break;
      case 'likes':
        query = query
          .order('likes', { ascending: false })
          .order('created_at', { ascending: false });
        break;
      case 'copies':
        query = query
          .order('copies', { ascending: false })
          .order('created_at', { ascending: false });
        break;
      case 'alphabetical':
        query = query.order('title', { ascending: true });
        break;
      case 'random':
        // For random sorting, we'll use a different approach
        const totalCount = await countTotalPrompts();
        const randomOffset = Math.floor(Math.random() * Math.max(0, totalCount - itemsPerPage));
        query = query
          .order('created_at', { ascending: false })
          .range(randomOffset, randomOffset + itemsPerPage - 1);
        break;
      case 'relevance':
        query = query
          .order('copies', { ascending: false })
          .order('reviews', { ascending: false })
          .order('likes', { ascending: false });
        break;
      default:
        // Default to recent
        query = query.order('created_at', { ascending: false });
        break;
    }
    
    // Add pagination (skip for random sort as it's handled above)
    if (sortBy !== 'random') {
      query = query.range(from, from + itemsPerPage - 1);
    }
    
    const { data, error, count } = await query;
    
    if (error) {
      console.error('Error fetching prompts:', error);
      throw error;
    }
    
    console.log(`Fetched ${data?.length || 0} prompts (total: ${count || 'unknown'})`);
    
    if (!data || data.length === 0) {
      console.log('No prompts found for the current filters');
      return [];
    }

    // Get unique user IDs from the prompts
    const userIds = data
      .map(item => item.user_id)
      .filter(Boolean);
    
    console.log(`Found ${userIds.length} unique user IDs`);
    
    // Fetch author profiles
    let profileMap = new Map();
    
    if (userIds.length > 0) {
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);
      
      if (profileError) {
        console.error('Error fetching profiles:', profileError);
      } else if (profiles) {
        console.log(`Fetched ${profiles.length} author profiles`);
        profiles.forEach(profile => {
          profileMap.set(profile.id, profile);
        });
      }
    }

    // Format the prompts
    return data.map(item => {
      const profile = profileMap.get(item.user_id) || {};
      return {
        id: item.id,
        title: item.title,
        description: item.description,
        content: item.content,
        category: item.categories?.name || 'Uncategorized',
        category_id: item.category_id,
        likes: item.likes || 0,
        reviews: item.reviews || 0,
        copies: item.copies || 0,
        author: {
          id: item.user_id || 'anonymous',
          name: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Anonymous'
        },
        createdAt: item.created_at,
        status: 'approved'
      };
    });
  } catch (error: any) {
    console.error('Error loading prompts:', error);
    toast.error('Failed to load prompts');
    return [];
  }
};
